import React, { useMemo, useState } from 'react';

import { Autocomplete, Box, TextField } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DOMPurify from 'dompurify';

import { onboardColors } from '../../theme/onboardTokens';
import { font } from '../styles';

import type { IApplicantQuestion } from '@host/components/NewUI/Components/Utilities/QuestionFieldGenerator';

interface IChoice {
  label: string;
  value: number;
}

interface Props {
  question: IApplicantQuestion;
  // The smart form's read-only answer helper is loosely typed; a select answer is a choice id string.
  answer?: unknown;
  disabled?: boolean;
  error?: string;
  onChange: (id: number, value: number | '') => void;
}

const styles = {
  label: {
    color: onboardColors.neutral.textBody,
    fontFamily: font,
    fontSize: 16,
    overflowWrap: 'anywhere',
    marginBottom: '8px'
  },
  error: {
    color: onboardColors.status.warningRedDeep,
    fontFamily: font,
    fontSize: 12,
    marginLeft: '4px'
  }
} as const;

// Matches the host select field it replaces: 290px wide, #DDDDDD border, 16px text.
const fieldSx = (hasError: boolean) => ({
  width: 290,
  margin: '3px',
  '& .MuiOutlinedInput-root': {
    padding: '0 10px',
    borderRadius: '6px',
    backgroundColor: onboardColors.surface.white,
    fontFamily: font,
    fontSize: 16,
    color: onboardColors.neutral.textStrong
  },
  '& .MuiOutlinedInput-root .MuiAutocomplete-input': { padding: '7px 0' },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? onboardColors.border.errorSoft : onboardColors.border.divider
  },
  '& .MuiOutlinedInput-root:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? onboardColors.border.errorSoft : onboardColors.border.divider
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    boxShadow: `${alpha(hasError ? onboardColors.status.warningRed : onboardColors.brand.primary, 0.25)} 0 0 0 2px`
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${hasError ? onboardColors.status.warningRed : onboardColors.brand.primary}`
  },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
    color: onboardColors.neutral.textDisabled
  }
});

/**
 * Searchable stand-in for the host's select question, used by the candidate
 * portal's smart form. Stores the chosen field_choice id, like the host field.
 */
const PortalSelectQuestion: React.FC<Props> = ({
  question,
  answer,
  disabled = false,
  error,
  onChange
}) => {
  const options = useMemo<IChoice[]>(
    () => question.field_choices.map((choice) => ({ label: choice.name, value: choice.id })),
    [question.field_choices]
  );
  const [value, setValue] = useState<IChoice | null>(
    () => options.find((option) => String(option.value) === String(answer ?? '')) ?? null
  );
  const inputId = `portal-select-question-${question.id}`;

  return (
    <Box>
      <Box component="label" htmlFor={inputId} sx={{ ...styles.label, display: 'block' }}>
        <span
          // Question titles are recruiter-authored HTML; sanitised as the host field does.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.title) }}
        />
        {question.required ? (
          <span style={{ color: onboardColors.status.errorSoft, marginLeft: 2 }}>*</span>
        ) : null}
      </Box>
      <Autocomplete
        disabled={disabled}
        getOptionLabel={(option) => option.label}
        id={inputId}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        noOptionsText="No matching options"
        options={options}
        sx={fieldSx(Boolean(error))}
        value={value}
        onChange={(_event, selected) => {
          setValue(selected);
          onChange(question.id, selected ? selected.value : '');
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search or select"
            slotProps={{
              ...params.slotProps,
              htmlInput: { ...params.slotProps.htmlInput, 'aria-invalid': Boolean(error) }
            }}
          />
        )}
        slotProps={{
          paper: { sx: { fontFamily: font, fontSize: 14, color: onboardColors.neutral.textStrong } }
        }}
      />
      {error ? <Box sx={styles.error}>{error}</Box> : null}
    </Box>
  );
};

export default PortalSelectQuestion;
